import React, { FC, useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  AccountTree,
  ArrowForward,
  CalendarToday,
  LocationOn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import clientService from 'src/clientService';
import { Project } from 'src/api/Api';

interface ProjectSelectionModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the picked project; without it the modal opens the project's overview. */
  onSelectProject?: (projectId: string) => void;
  selectedProjectId?: string;
  subtitle?: string;
}

const ProjectSelectionModal: FC<ProjectSelectionModalProps> = ({
  open,
  onClose,
  onSelectProject,
  selectedProjectId,
  subtitle = 'Choose a project to enter workspace',
}) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await clientService.api.readProjectsApiV1ProjectsGet({
        skip: 0,
        limit: 100,
      });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      void fetchProjects();
    }
  }, [open, fetchProjects]);

  const handleSelectProject = (projectId: string) => {
    onClose();
    if (onSelectProject) {
      onSelectProject(projectId);
    } else {
      navigate(`/project/${projectId}/overview`);
    }
  };

  const filteredProjects = projects.filter((p) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      p.project_name?.toLowerCase().includes(term) ||
      p.project_type?.toLowerCase().includes(term) ||
      p.country?.toLowerCase().includes(term) ||
      p.project_developer?.toLowerCase().includes(term)
    );
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#0F0F0F',
          backgroundImage: 'none',
          border: '1px solid #222',
          borderRadius: { xs: '12px', sm: '16px' },
          color: '#fff',
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.8), 0 0 1px 1px rgba(255, 255, 255, 0.05)',
          m: { xs: 1.5, sm: 2, md: 3 },
          width: { xs: 'calc(100% - 24px)', sm: '100%' },
          maxWidth: '820px',
          maxHeight: { xs: '90vh', sm: '85vh' },
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: '1px solid #1F1F1F',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              variant="rounded"
              sx={{
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                bgcolor: alpha('#10B981', 0.15),
                color: '#10B981',
              }}
            >
              <AccountTree sx={{ fontSize: { xs: 16, sm: 18 } }} />
            </Avatar>
            <Typography sx={{ fontSize: { xs: '16px', sm: '18px' }, fontWeight: 700, color: '#fff' }}>
              Select Project
            </Typography>
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: { xs: '11px', sm: '12px' }, color: '#888' }}>
            {subtitle}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: '#777',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255, 255, 255, 0.05)' },
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
        </IconButton>
      </DialogTitle>

      {/* Search Bar */}
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 }, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Filter projects by name, type, country..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#666', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#151515',
              color: '#fff',
              fontSize: { xs: '12px', sm: '13px' },
              borderRadius: '10px',
              '& fieldset': { borderColor: '#262626' },
              '&:hover fieldset': { borderColor: '#3a3a3a' },
              '&.Mui-focused fieldset': { borderColor: '#10B981' },
            },
          }}
        />
      </Box>

      {/* Content */}
      <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.5, sm: 2 }, minHeight: { xs: 260, sm: 340 }, maxHeight: { xs: '65vh', sm: '68vh' }, overflowY: 'auto' }}>
        {isLoading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={320} gap={2}>
            <CircularProgress size={32} sx={{ color: '#10B981' }} />
            <Typography sx={{ fontSize: '13px', color: '#888' }}>
              Loading projects from backend...
            </Typography>
          </Box>
        ) : filteredProjects.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={320} gap={1}>
            <Typography sx={{ fontSize: '14px', color: '#999', fontWeight: 600 }}>
              {searchTerm ? 'No matching projects found' : 'No projects available'}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#666' }}>
              {searchTerm ? 'Try a different search keyword' : 'No projects are assigned to your account.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredProjects.map((project) => {
              const isSelected = selectedProjectId === project.project_id;
              return (
                <Box
                  key={project.project_id}
                  onClick={() => handleSelectProject(project.project_id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: { xs: 1.25, sm: 1.75 },
                    borderRadius: '10px',
                    backgroundColor: isSelected ? 'rgba(16, 185, 129, 0.08)' : '#141414',
                    border: isSelected ? '1px solid rgba(16, 185, 129, 0.5)' : '1px solid #222',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    gap: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(16, 185, 129, 0.06)',
                      borderColor: 'rgba(16, 185, 129, 0.45)',
                      transform: { sm: 'translateX(4px)' },
                      '& .project-arrow': {
                        transform: { sm: 'translateX(3px)' },
                        color: '#10B981',
                      },
                      '& .project-title-text': {
                        color: '#10B981',
                      },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 2 }, minWidth: 0, flex: 1 }}>
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: { xs: 34, sm: 40 },
                        height: { xs: 34, sm: 40 },
                        bgcolor: alpha('#10B981', 0.1),
                        color: '#10B981',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                        flexShrink: 0,
                      }}
                    >
                      <AccountTree sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography
                          className="project-title-text"
                          sx={{
                            fontSize: { xs: '13px', sm: '14px' },
                            fontWeight: 700,
                            color: '#fff',
                            transition: 'color 0.15s ease',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: { xs: '170px', sm: '320px', md: '440px' },
                          }}
                        >
                          {project.project_name}
                        </Typography>

                        <Chip
                          label={project.project_type || 'Conservation'}
                          size="small"
                          sx={{
                            height: '18px',
                            fontSize: '10px',
                            fontWeight: 600,
                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                            color: '#10B981',
                            border: '1px solid rgba(16, 185, 129, 0.25)',
                          }}
                        />

                        {project.project_status && (
                          <Chip
                            label={project.project_status}
                            size="small"
                            sx={{
                              height: '18px',
                              fontSize: '10px',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              color: '#888',
                              border: '1px solid #282828',
                            }}
                          />
                        )}
                      </Box>

                      <Box display="flex" alignItems="center" gap={{ xs: 1.25, sm: 2 }} mt={0.5} flexWrap="wrap">
                        {project.country && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <LocationOn sx={{ fontSize: 12, color: '#666' }} />
                            <Typography sx={{ fontSize: '11px', color: '#777' }}>
                              {project.country}
                            </Typography>
                          </Box>
                        )}

                        {project.project_developer && (
                          <Typography sx={{ fontSize: '11px', color: '#666' }}>
                            Dev: <span style={{ color: '#888' }}>{project.project_developer}</span>
                          </Typography>
                        )}

                        {project.project_start_date && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <CalendarToday sx={{ fontSize: 11, color: '#666' }} />
                            <Typography sx={{ fontSize: '11px', color: '#777' }}>
                              {project.project_start_date}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <IconButton
                    className="project-arrow"
                    size="small"
                    sx={{
                      color: '#666',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      ml: 0.5,
                    }}
                  >
                    <ArrowForward sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProjectSelectionModal;
